/**
 * Maps v2 booking join API responses to the legacy nested shape consumed by the
 * join/reschedule UI (call cards, reschedule calendar, etc.).
 *
 * V1 has been decommissioned, so this is no longer bridging two live API
 * versions — it's a permanent internal shape-adapter that keeps the (still
 * widely used) legacy field names decoupled from the v2 API response shape.
 * Only worth removing alongside a dedicated migration of every consumer of
 * `TCustomerBookingResponse` to v2 field names directly.
 */

import {TBooking} from "../../redux/call/i-call";
import {TServiceApiResponse} from "../../redux/service/i-service";
import type {TCustomerBookingResponse} from "../booking";

type V2BookingJoinResponse = {
  token?: string | null;
  bookingToken?: string | null;
  roomId?: string | null;
  videoPlatform?: string;
  video_platform?: string;
  dailyCoUrl?: string | null;
  daily_co_url?: string | null;
  booking?: Partial<TBooking>;
  service?: Partial<TServiceApiResponse>;
  serviceName?: string;
  mentor?: TCustomerBookingResponse["mentor"];
  timeZone?: string;
  unavailableDates?: TCustomerBookingResponse["unavailableDates"];
  days?: TCustomerBookingResponse["days"];
  bookingTimeSlot?: string;
};

const EMPTY_DAYS: TCustomerBookingResponse["days"] = {
  sun: [],
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
};

function normalizeVideoPlatform(
  platform?: string
): TCustomerBookingResponse["videoPlatform"] {
  const normalized = platform?.toLowerCase() ?? "";

  if (normalized === "dailyco") return "dailyCo";
  if (normalized === "googlemeet") return "googleMeet";
  if (normalized === "custom") return "custom";
  return "videoSdk";
}

function createServiceStub(
  service?: Partial<TServiceApiResponse>,
  serviceName?: string
): TServiceApiResponse {
  return {
    id: service?.id ?? 0,
    userId: service?.userId ?? 0,
    name: service?.name ?? serviceName ?? "",
    description: service?.description ?? "",
    slug: service?.slug ?? "",
    callDuration: service?.callDuration ?? 0,
    callPrice: service?.callPrice ?? 0,
    callPriceCurrency: service?.callPriceCurrency ?? "",
    audioLocation: service?.audioLocation ?? false,
    videoLocation: service?.videoLocation ?? true,
    hideServiceFromMenu: service?.hideServiceFromMenu ?? false,
    request_phone_number: service?.request_phone_number ?? false,
    bookingQuestions: service?.bookingQuestions ?? null,
    booking_interval: service?.booking_interval ?? null,
    is_bundle: service?.is_bundle ?? false,
    no_of_sessions: service?.no_of_sessions ?? null,
    url_redirect: service?.url_redirect ?? null,
    tiny_url: service?.tiny_url ?? "",
    statusName: service?.statusName ?? "Active",
    status_id: service?.status_id ?? 1,
    createdAt: service?.createdAt ?? "",
    updatedAt: service?.updatedAt ?? "",
    currencies: service?.currencies ?? [],
    revenue: service?.revenue ?? [],
    enable_currency_conversion: service?.enable_currency_conversion ?? false,
  };
}

function createBookingStub(
  v2Data: V2BookingJoinResponse,
  roomId: string | null,
  videoPlatform: TCustomerBookingResponse["videoPlatform"]
): TBooking {
  const existing = v2Data.booking ?? {};

  return {
    id: existing.id ?? 0,
    reference: existing.reference ?? "",
    amount: existing.amount ?? 0,
    currency: existing.currency ?? "",
    booking_date: existing.booking_date ?? 0,
    booking_month: existing.booking_month ?? "",
    booking_year: existing.booking_year ?? "",
    booking_type: existing.booking_type ?? "video",
    bookingQuestions: existing.bookingQuestions ?? "[]",
    bundle_reference: existing.bundle_reference ?? null,
    bundle_session: existing.bundle_session ?? null,
    cancellationReason: existing.cancellationReason ?? null,
    coach_first_name: existing.coach_first_name ?? "",
    coach_last_name: existing.coach_last_name ?? "",
    coach_display_name: existing.coach_display_name ?? null,
    coach_from_time: existing.coach_from_time ?? "",
    coach_to_time: existing.coach_to_time ?? "",
    coach_username: existing.coach_username ?? "",
    createdAt: existing.createdAt ?? "",
    email_address: existing.email_address ?? "",
    endTime: existing.endTime ?? "",
    first_name: existing.first_name ?? "",
    is_bundle: existing.is_bundle ?? false,
    is_paid: existing.is_paid ?? false,
    last_name: existing.last_name ?? "",
    mentor_timezone: existing.mentor_timezone ?? "",
    notes: existing.notes ?? "",
    phone_number: existing.phone_number ?? null,
    roomId,
    service: createServiceStub(existing.service, v2Data.serviceName),
    serviceId: existing.serviceId ?? 0,
    statusId: existing.statusId ?? 1,
    statusName: existing.statusName ?? "Active",
    timeslot: existing.timeslot ?? "",
    updatedAt: existing.updatedAt ?? "",
    userId: existing.userId ?? 0,
    user_timezone: existing.user_timezone ?? "",
    video_platform: videoPlatform,
  };
}

export function normalizeV2BookingJoinResponse(
  v2Data: V2BookingJoinResponse
): TCustomerBookingResponse {
  const roomId = v2Data.roomId ?? v2Data.booking?.roomId ?? null;
  const videoPlatform = normalizeVideoPlatform(
    v2Data.videoPlatform ?? v2Data.video_platform ?? v2Data.booking?.video_platform
  );
  const bookingToken = v2Data.bookingToken ?? v2Data.token ?? null;
  const dailyCoUrl = v2Data.dailyCoUrl ?? v2Data.daily_co_url ?? "";

  return {
    booking: createBookingStub(v2Data, roomId, videoPlatform),
    service: createServiceStub(v2Data.service, v2Data.serviceName),
    mentor: v2Data.mentor ?? {
      id: 0,
      firstName: "",
      lastName: "",
      email: "",
      avatar: null,
      country: "",
      coverImage: null,
      isSocialAuth: false,
      statusId: 0,
      updatedAt: "",
    },
    timeZone: v2Data.timeZone ?? "",
    unavailableDates: v2Data.unavailableDates ?? [],
    days: v2Data.days ?? EMPTY_DAYS,
    bookingTimeSlot: v2Data.bookingTimeSlot ?? "",
    bookingToken,
    videoPlatform,
    dailyCoUrl,
  };
}

export function resolveGoogleMeetUrl(
  joinResponse: {data?: TCustomerBookingResponse},
  fallbackRoomId?: string | null
): string | null {
  return joinResponse.data?.booking?.roomId ?? fallbackRoomId ?? null;
}
